import React, { useState, useEffect } from "react";
import { PAGINATION_PAGE_SIZE_OPTIONS } from "../../config/pagination";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Chip,
  Avatar,
  LinearProgress,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import {
  ArrowBack,
  Download,
  FilterList,
  Business,
  People,
  School,
} from "@mui/icons-material";

const DepartmentsAnalytics = () => {
  const navigate = useNavigate();
  const { isInInstituteContext, selectedInstitute } = useSelector(
    (state) => state.instituteContext
  );
  const { user } = useSelector((state) => state.auth);
  const userRole = user?.role?.slug || user?.role || "student";
  const userInstitute = user?.institute?.name || "ABC Engineering College";
  const canViewGlobalData = userRole === "superadmin" && !isInInstituteContext;

  const [instituteFilter, setInstituteFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [institutes, setInstitutes] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      setLoading(true);
      try {
        const analyticsService = (await import("../../services/analyticsService")).default;
        const instituteService = (await import("../../services/instituteService")).default;

        if (canViewGlobalData) {
          const instRes = await instituteService.getInstitutes({ page: 1, limit: 50 });
          if (mounted && instRes?.data?.data) setInstitutes(instRes.data.data);
        }

        const res = await analyticsService.getDepartments({
          ...(instituteFilter && canViewGlobalData ? { instituteId: instituteFilter } : {}),
        });
        if (mounted) {
          const data = res?.data?.data ?? res?.data ?? [];
          setDepartments(Array.isArray(data) ? data : []);
        }
      } catch (e) {
        console.warn("Failed to load departments analytics:", e?.message || e);
        if (mounted) setDepartments([]);
      } finally {
        if (mounted) setLoading(false);
      }
    };
    load();
    return () => (mounted = false);
  }, [canViewGlobalData, instituteFilter]);

  const columns = [
    {
      field: "name",
      headerName: "Department",
      flex: 1,
      minWidth: 200,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Avatar sx={{ bgcolor: "primary.main", width: 36, height: 36 }}>
            <Business fontSize="small" />
          </Avatar>
          <Box>
            <Typography variant="body2" fontWeight={500}>
              {params.row.name}
            </Typography>
          </Box>
        </Box>
      ),
    },
    { field: "institute", headerName: "Institute", width: 200 },
    { field: "totalTeachers", headerName: "Teachers", width: 100 },
    { field: "totalStudents", headerName: "Students", width: 100 },
  ];

  const filteredDepartments = departments.filter(
    (d) => !statusFilter || (d.status || "active") === statusFilter
  );

  // Get page title based on context
  const getPageTitle = () => {
    if (canViewGlobalData) return "All Departments";
    if (isInInstituteContext) return `Departments - ${selectedInstitute?.name}`;
    return `Departments - ${userInstitute}`;
  };

  const getPageSubtitle = () => {
    if (canViewGlobalData) return "View all departments across institutes";
    if (isInInstituteContext)
      return `Departments overview for ${selectedInstitute?.name}`;
    return `Departments overview for your institute`;
  };

  const stats = [
    {
      label: "Total Departments",
      value: filteredDepartments.length,
      color: "primary.main",
      icon: <Business />,
    },
    {
      label: "Total Teachers",
      value: filteredDepartments.reduce((sum, d) => sum + (d.totalTeachers || 0), 0),
      color: "secondary.main",
      icon: <School />,
    },
    {
      label: "Total Students",
      value: filteredDepartments
        .reduce((sum, d) => sum + (d.totalStudents || 0), 0)
        .toLocaleString(),
      color: "success.main",
      icon: <People />,
    },
  ];

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
        <IconButton onClick={() => navigate(-1)}>
          <ArrowBack />
        </IconButton>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h5" fontWeight={600}>
            {getPageTitle()}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {getPageSubtitle()}
          </Typography>
        </Box>
        <Button variant="outlined" startIcon={<Download />}>
          Export
        </Button>
      </Box>

      {/* Stats */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {stats.map((stat, i) => (
          <Grid item xs={6} sm={3} key={i}>
            <Card>
              <CardContent>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      {stat.label}
                    </Typography>
                    <Typography
                      variant="h4"
                      fontWeight={700}
                      sx={{ color: stat.color }}
                    >
                      {stat.value}
                    </Typography>
                  </Box>
                  <Avatar sx={{ bgcolor: stat.color, width: 48, height: 48 }}>
                    {stat.icon}
                  </Avatar>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
            <FilterList color="action" />
            <Typography variant="subtitle1" fontWeight={600}>
              Filters
            </Typography>
          </Box>
          <Grid container spacing={2}>
            {canViewGlobalData && (
              <Grid item xs={12} sm={6} md={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>Institute</InputLabel>
                  <Select
                    value={instituteFilter}
                    label="Institute"
                    onChange={(e) => setInstituteFilter(e.target.value)}
                  >
                    <MenuItem value="">All Institutes</MenuItem>
                    {institutes.map((inst) => (
                      <MenuItem key={inst.id} value={String(inst.id)}>
                        {inst.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  label="Status"
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <MenuItem value="">All Status</MenuItem>
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {loading && <LinearProgress sx={{ mb: 2 }} />}

      {/* Data Grid */}
      <Card>
        <CardContent>
          <DataGrid
            rows={filteredDepartments}
            columns={columns}
            autoHeight
            disableRowSelectionOnClick
            pageSizeOptions={PAGINATION_PAGE_SIZE_OPTIONS}
            initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
            sx={{ border: "none" }}
          />
        </CardContent>
      </Card>
    </Box>
  );
};

export default DepartmentsAnalytics;
